export const BROKER_STATUSES = [
  "active",
  "activation_pending",
  "questionnaire_pending",
  "disabled",
] as const;

export type BrokerStatus = (typeof BROKER_STATUSES)[number];

export const BROKER_STATUS_LABELS: Record<BrokerStatus, string> = {
  active: "Activo",
  activation_pending: "Pendiente activación",
  questionnaire_pending: "Cuestionario pendiente",
  disabled: "Deshabilitado",
};

export const BROKER_STATUS_STYLES: Record<
  BrokerStatus,
  { bg: string; fg: string; border: string }
> = {
  active: { bg: "#d4edda", fg: "#155724", border: "#c3e6cb" },
  activation_pending: { bg: "#fff3cd", fg: "#856404", border: "#ffe69c" },
  questionnaire_pending: { bg: "#cfe2ff", fg: "#084298", border: "#b6d4fe" },
  disabled: { bg: "#f1f3f5", fg: "#495057", border: "#dee2e6" },
};

export type BrokerOwnerEmployee = {
  id: number;
  name: string | null;
  lastname: string | null;
  email: string | null;
  phone: string | null;
  lastSeenAt: string | null;
};

export type BrokerListRow = {
  brokerInternalId: number;
  brokerName: string;
  businessName: string | null;
  brokerEmail: string | null;
  brokerPhone: string | null;
  brokerIdCard: string;
  brokerIdDgsfp: string;
  status: BrokerStatus;
  markets: string[];
  medorCode: string | null;
  themeColor: string | null;
  logoFilename: string | null;
  externalId: string | null;
  questionnaireCompletedAt: string | null;
  termsAcceptedAt: string | null;
  rcAcceptedAt: string | null;
  dgsAuthAcceptedAt: string | null;
  ownerEmployee: BrokerOwnerEmployee | null;
  employeesEnabledCount: number;
  leadsCount: number;
};

export type BrokerListResponse = {
  items: BrokerListRow[];
  total: number;
  page: number;
  pageSize: number;
};

export type BrokerDetail = {
  brokerInternalId: number;
  brokerName: string;
  businessName: string | null;
  brokerEmail: string | null;
  brokerPhone: string | null;
  brokerIdCard: string;
  brokerIdDgsfp: string;
  address: string | null;
  status: BrokerStatus;
  markets: string[];
  medorCode: string | null;
  themeColor: string | null;
  complementaryUrl: string | null;
  logoFilename: string | null;
  htmlCode: string | null;
  externalId: string | null;
  slug: string | null;
  brokerProviderId: number | null;
  questionnaireCompletedAt: string | null;
  termsAcceptedAt: string | null;
  rcAcceptedAt: string | null;
  dgsAuthAcceptedAt: string | null;
};

export type BrokerStats = {
  employeesTotal: number;
  employeesEnabled: number;
  leadsTotal: number;
  leadsComplete: number;
  leadsCanceled: number;
  invoiceDocsTotal: number;
};

export type BrokerDetailResponse = {
  broker: BrokerDetail;
  stats: BrokerStats;
};

export const EMPLOYEE_STATUSES = [
  "enabled",
  "disabled",
  "deleted",
  "guested",
] as const;

export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const EMPLOYEE_STATUS_LABELS: Record<EmployeeStatus, string> = {
  enabled: "Activo",
  disabled: "Deshabilitado",
  deleted: "Eliminado",
  guested: "Invitado",
};

export const EMPLOYEE_STATUS_STYLES: Record<
  EmployeeStatus,
  { bg: string; fg: string; border: string }
> = {
  enabled: { bg: "#d4edda", fg: "#155724", border: "#c3e6cb" },
  disabled: { bg: "#fde2e2", fg: "#b32424", border: "#f5c6cb" },
  deleted: { bg: "#f1f3f5", fg: "#495057", border: "#dee2e6" },
  guested: { bg: "#fff3cd", fg: "#856404", border: "#ffe69c" },
};

export type BrokerEmployee = {
  id: number;
  ulid: string | null;
  brokerId: number;
  name: string | null;
  lastname: string | null;
  email: string;
  phone: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  type: "owner" | "worker" | string;
  status: EmployeeStatus | string;
  roles: string[];
  externalId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  lastSeenAt: string | null;
};

export type BrokerEmployeesResponse = {
  items: BrokerEmployee[];
  total: number;
};

export type BrokerInvoiceDocument = {
  invoiceDocumentId: string;
  name: string;
  year: number | null;
  month: number | null;
  extension: string | null;
  amount: number;
  mimetype: string | null;
  createdAt: string | null;
};

export type BrokerInvoicesResponse = {
  items: BrokerInvoiceDocument[];
  total: number;
  amountTotal: number;
};
